import { Box, Container, Typography, Button, Card, CardContent, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HeroSection } from '../../components/marketing/HeroSection';
import LanguageIcon from '@mui/icons-material/Language';
import EmailIcon from '@mui/icons-material/Email';
import TextsmsIcon from '@mui/icons-material/Textsms';
import PhoneIcon from '@mui/icons-material/Phone';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SavingsIcon from '@mui/icons-material/Savings';

const isDevMode = window.location.protocol === 'http:';

const pricingPlans = [
  {
    name: 'Small',
    requests: 3,
    price: 35,
    stripeUrl: isDevMode
      ? 'https://buy.stripe.com/test_5kA4gXbpcfjC4sE4gi'
      : 'https://buy.stripe.com/8wM3cQepKaZKf0A5kt',
  },
  { name: 'Medium', requests: 5, price: 55, recommended: true, stripeUrl: 'https://buy.stripe.com/eVafZC81m9VGaKk8wG' },
  { name: 'Large', requests: 15, price: 125, stripeUrl: 'https://buy.stripe.com/aEU9Be0yU7Nyf0A4gr' },
];

export const PlansPage = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <HeroSection
        title="How often do you need a hand?"
        subtitle="Standard requests are up to 20 minutes of work"
        variant="gradient"
      />

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={4} justifyContent="center">
          {pricingPlans.map((plan) => (
            <Grid key={plan.name} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  overflow: 'visible',
                  border: plan.recommended ? '2px solid' : '1px solid',
                  borderColor: plan.recommended ? 'primary.main' : 'divider',
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    MOST POPULAR
                  </Box>
                )}
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    ${plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    per month
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {plan.requests} requests
                  </Typography>
                  <Button
                    variant={plan.recommended ? 'contained' : 'outlined'}
                    fullWidth
                    size="large"
                    href={plan.stripeUrl}
                    sx={{ mt: 2 }}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* What's Included Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 4 }}>
            All Plans Include...
          </Typography>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon><LanguageIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Submit requests via website" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Submit requests via email" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TextsmsIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Submit requests via text" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Submit requests via phone call" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon><AutorenewIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Your requests roll over" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Refer friends, get free months" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><BarChartIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Tons of usage stats" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon><IntegrationInstructionsIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="3rd party integrations" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><SupportAgentIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="VIP customer service" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><SavingsIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Huge savings" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Looking for more? */}
      <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Looking for more?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get a dedicated assistant who learns your preferences and handles your tasks every day.
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/dedicated-assistant')}
          >
            Learn About Dedicated Assistants
          </Button>
        </Container>
      </Box>
    </Box>
  );
};
